import React, { useEffect } from "react";
import { Sparkles, CheckCircle2, TrendingUp, ArrowRight, Lock, Brain, MessageSquareText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEntitlement } from "@/lib/entitlement-context";

/**
 * FreeSummaryScreen - shown to Free users immediately after they finalize
 * their 1st Official AI Interview (or Practice Interview). Deliberately qualitative-only:
 * strengths, growth opportunities, and personalized coaching - no scores,
 * no rankings, no readiness numbers. Those stay exclusive to Premium so
 * the upgrade delivers a clear "here is where you actually stand" moment.
 *
 * Props:
 *  - report: the raw report object from GET /video-interview/{id}
 *  - onContinue: callback invoked when user clicks "Continue to Question 2"
 *      - parent decides whether to open UpgradeScreen (default) or navigate.
 */
export default function FreeSummaryScreen({ report, onContinue }) {
  const { track } = useEntitlement();
  const r = report || {};

  useEffect(() => {
    track("free.summary_viewed", {
      has_coaching: !!(r.coaching || r.hiring_manager_summary),
      strengths_count: (r.strengths || []).length,
      improvements_count: (r.improvements || []).length,
    });
  }, [track, r.coaching, r.hiring_manager_summary, r.strengths, r.improvements]);

  const strengths = (r.strengths || r.top_strengths || []).slice(0, 3);
  const improvements = (r.improvements || r.top_improvements || []).slice(0, 3);
  const coaching = (r.coaching || "").trim();

  return (
    <div data-testid="free-summary-screen" className="max-w-3xl mx-auto animate-in space-y-8 py-4">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] text-amber-400 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-2.5 py-1 mb-4">
          <Sparkles className="h-3 w-3" />
          Free Interview Summary
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-50">
          Great job!
        </h1>
        <p className="mt-3 text-base text-zinc-400 max-w-lg mx-auto leading-relaxed">
          Here&apos;s what we&apos;ve learned so far. You&apos;re off to a strong start. Several
          real strengths employers look for came through in your response.
        </p>
      </div>

      {/* Strengths */}
      {strengths.length > 0 && (
        <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.03] p-5 sm:p-6">
          <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-400 mb-3">
            What worked well
          </div>
          <ul className="space-y-2.5 text-sm text-zinc-100 leading-relaxed">
            {strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Growth opportunities */}
      {improvements.length > 0 && (
        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.03] p-5 sm:p-6">
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-3">
            Where to grow next
          </div>
          <ul className="space-y-2.5 text-sm text-zinc-100 leading-relaxed">
            {improvements.map((s, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <TrendingUp className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* AI coaching narrative */}
      {coaching && (
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5 sm:p-6">
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 mb-3">
            AI coaching · personalized to you
          </div>
          <p className="text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap">{coaching}</p>
        </div>
      )}

      {/* AI Coaching Preview, blurred teaser cards that glimpse the depth of
          Premium coaching. Tap → /app/upgrade with analytics. */}
      <CoachingPreviewTeaser />

      {/* What's still locked, set the aspiration */}
      <div
        data-testid="free-summary-locked-strip"
        className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] via-zinc-900/40 to-zinc-950/60 p-5 sm:p-6"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full border border-amber-500/40 bg-amber-500/[0.10] flex items-center justify-center flex-shrink-0">
            <Lock className="h-4 w-4 text-amber-300" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400">
              Still to unlock
            </div>
            <div className="text-sm text-zinc-100 font-semibold mt-0.5">
              You&apos;re off to a strong start.
            </div>
            <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">
              Based on your first response, you&apos;ve demonstrated several strengths employers
              look for. We&apos;ve only assessed a small portion of your interview readiness so far.
              Continue preparing to unlock your Official Interview Readiness Score, identify your
              highest-impact improvement opportunities, and maximize your chances of receiving an offer.
            </p>
          </div>
        </div>
      </div>

      {/* Primary CTA */}
      <div className="flex flex-col items-center gap-2 pt-2">
        <button
          type="button"
          onClick={() => {
            track("upgrade.continue_clicked", { trigger: "free_summary" });
            onContinue && onContinue();
          }}
          data-testid="free-summary-continue"
          className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-6 py-3 shadow-[0_0_32px_-8px_rgba(245,158,11,0.7)] transition-colors group"
        >
          Continue to Question 2
          <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
        </button>
        <div className="text-[11px] text-zinc-500">
          Keep momentum. Unlock unlimited practice + your Official Readiness Score.
        </div>
      </div>
    </div>
  );
}


/**
 * CoachingPreviewTeaser - two blurred AI coaching cards on the Free Summary
 * that hint at the depth of feedback Premium users receive. Titles are
 * readable; the body copy is intentionally blurred with a lock overlay so
 * users see the *shape* of coaching without spoiling the value. Every
 * interaction routes to `/app/upgrade` with an analytics event.
 */
function CoachingPreviewTeaser() {
  const navigate = useNavigate();
  const { track } = useEntitlement();

  useEffect(() => {
    track("free.coaching_preview_viewed", { placement: "free_summary" });
  }, [track]);

  const CARDS = [
    {
      key: "star_breakdown",
      title: "STAR Story Breakdown",
      icon: Brain,
      fauxLines: [
        "Situation: You framed the context concisely, mention…",
        "Task: Sharpen the stakes by naming the metric that…",
        "Action: Lead with the decision only you owned…",
        "Result: Quantify the outcome, investors respond to…",
      ],
      accent: "from-violet-500/20 to-violet-400/5",
      badge: "8-point analysis",
    },
    {
      key: "recruiter_pov",
      title: "Recruiter's Point of View",
      icon: MessageSquareText,
      fauxLines: [
        "A senior hiring manager would flag your delivery on…",
        "The word choice around ownership signals…",
        "Confidence: strong opener, drop the hedging in…",
        "Recommended rewrite: swap the passive framing for…",
      ],
      accent: "from-amber-500/20 to-amber-400/5",
      badge: "Live recruiter lens",
    },
  ];

  const open = (cardKey) => {
    track("free.coaching_preview_clicked", { card: cardKey });
    navigate("/app/upgrade");
  };

  return (
    <div data-testid="free-coaching-preview" className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 inline-flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> A peek at your Premium coaching
        </div>
        <span className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-1.5 py-0.5">
          Premium
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => open(c.key)}
              data-testid={`free-coaching-preview-${c.key}`}
              className="group relative overflow-hidden rounded-2xl border border-zinc-800/80 bg-zinc-900/40 hover:border-amber-500/40 transition-colors text-left"
            >
              <div
                aria-hidden
                className={`absolute inset-0 bg-gradient-to-br ${c.accent} opacity-60 pointer-events-none`}
              />
              <div className="relative p-4 sm:p-5 space-y-3">
                <div className="flex items-center gap-2.5">
                  <span className="h-8 w-8 rounded-lg border border-amber-500/30 bg-amber-500/[0.08] flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4 text-amber-300" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-zinc-50 truncate">{c.title}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      {c.badge}
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <div
                    aria-hidden
                    className="space-y-1.5 text-[13px] leading-relaxed text-zinc-200 select-none"
                    style={{ filter: "blur(5px)" }}
                  >
                    {c.fauxLines.map((l, i) => (
                      <div key={i}>{l}</div>
                    ))}
                  </div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                    <span className="h-9 w-9 rounded-full border border-amber-500/40 bg-amber-500/[0.10] flex items-center justify-center backdrop-blur-sm">
                      <Lock className="h-4 w-4 text-amber-300" />
                    </span>
                    <div className="text-[10px] uppercase tracking-widest text-amber-300/90 opacity-0 group-hover:opacity-100 transition-opacity">
                      Unlock full analysis →
                    </div>
                  </div>
                </div>
                <div className="text-[11px] text-zinc-400 leading-relaxed">
                  Premium unlocks a personalized {c.title.toLowerCase()} after every mock,
                  frame-by-frame, story-by-story.
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
